import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Badge, Row, Col, Label } from 'reactstrap';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { SearchTab, Medicine, SaleInvoiceFields } from './searchTypes';
import * as Yup from 'yup';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useCreateMutation } from 'helpers/http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'helpers/http-client/httpConstants';
import { Tab } from 'store/sales-invoice/medicineSearch.reducer';

interface Props {
  tab: Tab;
  isUrdu: boolean;
  getCheaperEquivalent: (medicine: Medicine) => Medicine | undefined;
  onSwapMedicine: (tabId: string, currentMedId: string, replacement: Medicine) => void;
  handleRemoveMedicine: (medicineId: string) => void;
  onUnitChange: (medicineId: string, units: number) => void;
  printInvoice: (invoiceData: any) => void;
}

const validationSchema = Yup.object({
  customerName: Yup.string().optional(),
  customerPhoneNumber: Yup.string().optional(),
  totalAmount: Yup.number().required('Total amount is required'),
  totalDiscount: Yup.number().default(0),
  shippingCharges: Yup.number().default(0),
  items: Yup.array()
    .of(
      Yup.object({
        medicineId: Yup.string().required('Medicine ID is required'),
        quantity: Yup.number().min(1).required('Quantity is required'),
        unitPrice: Yup.number().min(0).required('Unit price is required'),
        discount: Yup.number().min(0).max(100, 'Discount cannot exceed 100%').optional(),
        totalPrice: Yup.number().min(0).required('Total price is required'),
      }),
    )
    .min(1, 'At least one medicine is required'),
});

const MedicineTable: React.FC<Props> = ({
  tab,
  isUrdu,
  getCheaperEquivalent,
  onSwapMedicine,
  onUnitChange,
  handleRemoveMedicine,
}) => {
  const { t } = useTranslation();
  const [unitPrices, setUnitPrices] = useState<{ [id: string]: number }>({});
  const [discounts, setDiscounts] = useState<{ [id: string]: number }>({});
  const [shippingCharges, setShippingCharges] = useState<number>(0);
  const [totalDiscount, setTotalDiscount] = useState<number>(0);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const { mutate: createMedicine } = useCreateMutation(API_ENDPOINTS.SALES_INVOICE);

  useEffect(() => {
    const now = new Date();
    const formatted =
      now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0') +
      '-' +
      String(now.getHours()).padStart(2, '0') +
      String(now.getMinutes()).padStart(2, '0') +
      String(now.getSeconds()).padStart(2, '0');
    setInvoiceNumber(`INV-${formatted}`);
  }, []);

  useEffect(() => {
    const totalDisc = tab?.selectedMedicines?.reduce((sum, item) => {
      const id = item.id;
      const price = unitPrices[id] ?? item.latestPrice;
      const discount = discounts[id] ?? 0;
      const quantity = item.units;
      const itemDisc = (price * quantity * discount) / 100;
      return sum + itemDisc;
    }, 0);
    setTotalDiscount(totalDisc);
  }, [discounts, unitPrices, tab?.selectedMedicines]);

  if (tab?.selectedMedicines?.length === 0) return null;

  const initialValues = {
    customerName: '',
    customerPhoneNumber: '',
    totalAmount: 0,
    totalDiscount,
    shippingCharges,
    items: tab?.selectedMedicines?.map((item) => {
      const id = item.id;
      const price = unitPrices[id] ?? item.latestPrice;
      const discount = discounts[id] ?? 0;
      const quantity = item.units;
      const totalPrice = price * quantity * (1 - discount / 100);

      return {
        medicineId: id,
        quantity,
        unitPrice: price,
        discount,
        totalPrice,
      };
    }),
  };

  const handleSubmit = (values: SaleInvoiceFields) => {
    const payload = {
      invoiceNumber,
      ...values,
      totalAmount: subtotal + (values.shippingCharges || 0),
    };

    createMedicine(payload);
  };

  const subtotal = initialValues?.items?.reduce((sum, i) => sum + i.totalPrice, 0);
  const totalAmount = subtotal + shippingCharges;

  return (
    <Formik
      initialValues={{ ...initialValues, totalAmount }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <h3 className="fw-bold" style={{ textAlign: isUrdu ? 'right' : 'left' }}>
            {t('Invoice Information')}
          </h3>

          <Row className="mb-3">
            <Col md={4}>
              <Label>{t('Invoice Number')}</Label>
              <Input value={invoiceNumber} disabled />
            </Col>
            <Col md={4}>
              <Label>{t('Customer Name')}</Label>
              <Field
                name="customerName"
                className="form-control"
                placeholder={t('Enter customer name')}
              />
              <ErrorMessage name="customerName" className="text-danger small" component="div" />
            </Col>
            <Col md={4}>
              <Label>{t('Customer Phone')}</Label>
              <Field
                name="customerPhoneNumber"
                className="form-control"
                placeholder={t('Enter phone number')}
              />
              <ErrorMessage
                name="customerPhoneNumber"
                className="text-danger small"
                component="div"
              />
            </Col>
          </Row>

          <h3 className="fw-bold" style={{ textAlign: isUrdu ? 'right' : 'left' }}>
            {t('Order Summary')}
          </h3>

          <div className="table-responsive">
            <Table
              bordered
              striped
              className={classnames({ 'text-end': isUrdu, 'table-sm': true })}
            >
              <thead className="table-light">
                <tr>
                  {[
                    t('No.'),
                    t('Medicine Name'),
                    t('Stock available'),
                    t('Unit Price (PKR)'),
                    t('Units'),
                    t('Discount (%)'),
                    t('Total Price (PKR)'),
                    '',
                  ].map((label, idx) => (
                    <th key={idx} className="fw-bold">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tab?.selectedMedicines?.map((item, idx) => {
                  const id = item.id;
                  const cheaper = null;
                  const price = unitPrices[id] ?? item.latestPrice;
                  const discount = discounts[id] ?? 0;
                  const quantity = item.units;
                  const total = price * quantity * (1 - discount / 100);
                  console.log(item);
                  return (
                    <tr key={id}>
                      <td className="fw-bold text-end">{idx + 1}</td>
                      <td className="fw-bold">{item.name} </td>
                      <td className="text-end">{item.availableStockQuantity}</td>
                      <td>
                        <Input
                          type="number"
                          min={0}
                          step={'0.01'}
                          value={price}
                          onChange={(e) => {
                            const newPrice = parseFloat(e.target.value) || 0;
                            setUnitPrices((prev) => ({ ...prev, [id]: newPrice }));
                          }}
                          className="form-control form-control-sm"
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min={1}
                          max={item.availableStockQuantity}
                          value={quantity}
                          onChange={(e) => onUnitChange(id, parseInt(e.target.value) || 1)}
                          className="form-control form-control-sm"
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={discount}
                          onChange={(e) => {
                            let val = parseFloat(e.target.value);
                            if (isNaN(val)) val = 0;
                            if (val > 100) val = 100;
                            if (val < 0) val = 0;
                            setDiscounts((prev) => ({ ...prev, [id]: val }));
                          }}
                          className="form-control form-control-sm"
                          title="Enter discount percentage (0–100)"
                        />
                      </td>
                      <td className="text-end fw-bold">{total.toFixed(2)}</td>
                      <td className="text-center">
                        <Badge
                          color="danger"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRemoveMedicine(id)}
                          title={t('Remove')}
                        >
                          {t('Remove')}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}

                <tr>
                  <td colSpan={7} className="text-end fw-bold">
                    {t('Sub Total')}
                  </td>
                  <td className="text-end fw-bold">{subtotal?.toFixed(2)}</td>
                </tr>

                <tr>
                  <td colSpan={7} className="text-end fw-bold">
                    {t('Shipping')}
                  </td>
                  <td>
                    <Field
                      name="shippingCharges"
                      as={Input}
                      type="number"
                      min={0}
                      value={shippingCharges}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = parseFloat(e.target.value) || 0;
                        setShippingCharges(val);
                        setFieldValue('shippingCharges', val);
                      }}
                      className="form-control form-control-sm text-end"
                    />
                  </td>
                </tr>

                <tr>
                  <td colSpan={7} className="text-end fw-bold">
                    {t('Total Discount')}
                  </td>
                  <td>
                    <Field
                      name="totalDiscount"
                      as={Input}
                      type="number"
                      min={0}
                      value={totalDiscount?.toFixed(2)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const val = parseFloat(e.target.value) || 0;
                        setTotalDiscount(val);
                        setFieldValue('totalDiscount', val);
                      }}
                      className="form-control form-control-sm text-end"
                    />
                  </td>
                </tr>

                <tr>
                  <td colSpan={7} className="text-end fw-bold">
                    {t('Total')}
                  </td>
                  <td className="text-end">
                    <h4 className="m-0 fw-bold">{totalAmount.toFixed(2)} PKR</h4>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-end mt-3">
            <Button color="primary" type="submit">
              {t('Print Invoice')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default MedicineTable;
